import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Input,
  MessageBar,
  MessageBarBody,
} from "@fluentui/react-components";
import { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTagsCreate } from "../../hooks/useTags";
import { CreateTagRequest, FlatVsaaTag } from "../../models/VsaaTag";
import {
  getParentIdFromAncestors,
  hasDuplicateNameAtSameLevel,
} from "../../services/TagService";
import { ButtonWithLoading } from "../ButtonWithLoading";

interface CreateTreeItemDialogProps {
  open: boolean;
  tagCollectionId: string;
  title: string;
  ancestorIds: string[] | undefined;
  allTags: FlatVsaaTag[];
  onClose: () => void;
  onSave: () => void;
}

export const CreateTreeItemDialog = (props: CreateTreeItemDialogProps) => {
  const {
    open,
    ancestorIds,
    onClose,
    tagCollectionId,
    title,
    onSave,
    allTags,
  } = props;
  const [newTagName, setNewTagName] = useState<string>("");
  const [duplicateError, setDuplicateError] = useState<boolean>(false);
  const { mutateAsync: createTag, isPending: createTagPending } =
    useTagsCreate(tagCollectionId);

  const { t } = useTranslation();

  const onNewTagInputChange = (ev: ChangeEvent<HTMLInputElement>) => {
    const value = ev.target.value;
    setNewTagName(value);

    if (value.trim()) {
      const parentId = getParentIdFromAncestors(ancestorIds);
      const isDuplicate = hasDuplicateNameAtSameLevel(value, parentId, allTags);
      setDuplicateError(isDuplicate);
    } else {
      setDuplicateError(false);
    }
  };

  const onClickCreate = async () => {
    if (!newTagName || duplicateError) {
      return;
    }

    const request: CreateTagRequest = {
      title: newTagName,
      ancestorIds: ancestorIds,
    };

    await createTag(request);
    setNewTagName(""); // Clearing the value for new tags here since it will otherwise be retained when the form is opened again.
    setDuplicateError(false);
    onSave();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !createTagPending) {
      onClickCreate();
    }
  };

  return (
    <Dialog open={open}>
      <DialogSurface aria-describedby={undefined} style={{ minWidth: "650px" }}>
        <DialogBody>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent>
            {duplicateError && (
              <MessageBar intent="info">
                <MessageBarBody>{t("duplicateTagNameError")}</MessageBarBody>
              </MessageBar>
            )}
            <Input
              value={newTagName}
              onChange={onNewTagInputChange}
              onKeyDown={handleKeyDown}
            />
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <ButtonWithLoading
                text={t("create")}
                disabled={createTagPending || !newTagName || duplicateError}
                onClick={onClickCreate}
                isLoading={createTagPending}
              />
            </DialogTrigger>
            <DialogTrigger disableButtonEnhancement>
              <Button
                appearance="secondary"
                disabled={createTagPending}
                onClick={onClose}
              >
                {t("cancel")}
              </Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
